import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, User, LogOut, LayoutDashboard, Heart, Sparkles, ChevronLeft, ChevronRight, PackageX } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { MobileNav } from './MobileNav';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { SYMBOLS } from '../lib/currency';
import { AffiliateDisclosure } from './AffiliateDisclosure';
import { EsimCrossSellModal } from './EsimCrossSellModal';
import brandIcon from '../assets/flightdeals-icon.png';

const NAV_FALLBACK = [
  { label: 'Flights', to: '/flights' },
  { label: 'Hotels', to: '/hotels' },
  { label: 'Experiences', to: '/experiences' },
  { label: 'Visa', to: '/visa' },
  { label: 'Store', to: '/store' },
  { label: 'eSIM', to: '/esim' },
  { label: 'Transfers', to: '/transfers' },
  { label: 'Car Rentals', to: '/car-rentals' },
  { label: 'Insurance', to: '/insurance' },
  { label: 'Tours', to: '/tours' },
  { label: 'Itineraries', to: '/itineraries' },
  { label: 'Creators', to: '/creators' },
  { label: 'Deals', to: '/deals' },
  { label: 'AI Planner', to: '/ai-travel-planner' },
];

const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'JPY', 'SGD', 'AED', 'THB', 'AUD'];

// Single-row, horizontally-scrollable primary nav (no wrapping). Touch/trackpad
// swipe works natively; desktop/tablet get left/right arrows; the active vertical
// is auto-scrolled into view. Preserves the existing header styling.
const ScrollableNav = ({ nav }) => {
  const scroller = useRef(null);
  const location = useLocation();
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = scroller.current;
    if (!el) return undefined;
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => { el.removeEventListener('scroll', update); window.removeEventListener('resize', update); };
  }, [update]);

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const active = el.querySelector('[data-active="true"]');
    if (active) {
      const left = active.offsetLeft - el.clientWidth / 2 + active.offsetWidth / 2;
      el.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
    }
    update();
  }, [location.pathname, update]);

  const nudge = (dir) => {
    const el = scroller.current;
    if (el) el.scrollBy({ left: dir * Math.max(200, el.clientWidth * 0.6), behavior: 'smooth' });
  };

  return (
    <div className="relative hidden md:flex items-center min-w-0 flex-1 mx-2">
      {canLeft && (
        <button type="button" aria-label="Scroll navigation left" onClick={() => nudge(-1)}
          className="absolute left-0 z-10 grid place-items-center h-8 w-8 rounded-full bg-primary text-primary-foreground shadow-md ring-1 ring-white/15 hover:bg-white/10" data-testid="nav-arrow-left">
          <ChevronLeft className="h-4 w-4" />
        </button>
      )}
      <nav ref={scroller}
        className="flex items-center gap-1 overflow-x-auto scroll-smooth px-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        data-testid="header-primary-nav">
        {nav.map((n) => {
          const active = location.pathname === n.to || location.pathname.startsWith(n.to + '/');
          return (
            <Link key={n.to} to={n.to} data-active={active}
              className={`shrink-0 whitespace-nowrap px-3 py-2 rounded-lg text-sm font-medium transition-colors ${active ? 'bg-white/15 text-primary-foreground' : 'text-primary-foreground/85 hover:text-primary-foreground hover:bg-white/10'}`}
              data-testid={`nav-${n.label.toLowerCase().replace(' ', '-')}`}>
              {n.label}
            </Link>
          );
        })}
      </nav>
      {canRight && (
        <button type="button" aria-label="Scroll navigation right" onClick={() => nudge(1)}
          className="absolute right-0 z-10 grid place-items-center h-8 w-8 rounded-full bg-primary text-primary-foreground shadow-md ring-1 ring-white/15 hover:bg-white/10" data-testid="nav-arrow-right">
          <ChevronRight className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};

export const Header = () => {
  const { currency, setCurrency, navItems } = useApp();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  // Config-driven NAV (Admin site-config is the source of truth); falls back to defaults.
  const nav = (navItems || NAV_FALLBACK).map((n) => ({ label: n.label, to: n.route || n.to }));

  return (
    <header className="sticky top-0 z-50 bg-primary text-primary-foreground border-b border-white/10 backdrop-blur supports-[backdrop-filter]:bg-primary/95">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-16 flex items-center justify-between gap-2 sm:gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0 min-w-0" data-testid="brand-logo">
            <img src={brandIcon} alt="FlightDeals.in logo" className="h-8 sm:h-9 w-auto shrink-0" width="55" height="36" />
            <div className="leading-none min-w-0">
              <div className="font-display font-semibold text-base sm:text-lg tracking-tight truncate">FlightDeals<span className="text-accent">.in</span></div>
              <div className="hidden sm:block text-[10px] text-primary-foreground/60 tracking-wide">COMPARE · DISCOVER · SAVE</div>
            </div>
          </Link>

          <ScrollableNav nav={nav} />

          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger className="h-9 w-[58px] sm:w-[92px] px-2 bg-white/10 border-white/15 text-primary-foreground focus:ring-accent" data-testid="currency-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`currency-option-${c}`}>{SYMBOLS[c]} {c}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="hidden sm:flex h-9 gap-2 text-primary-foreground hover:bg-white/10" data-testid="header-account-menu">
                    <span className="grid place-items-center h-6 w-6 rounded-full bg-accent text-accent-foreground text-xs font-semibold">
                      {(user.name || 'U').charAt(0).toUpperCase()}
                    </span>
                    <span className="hidden sm:inline max-w-[90px] truncate text-sm">{user.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem onClick={() => navigate('/account')} data-testid="menu-account">
                    <Heart className="h-4 w-4 mr-2" /> My Account
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/creator/dashboard')} data-testid="menu-creator">
                    <Sparkles className="h-4 w-4 mr-2" /> Creator Studio
                  </DropdownMenuItem>
                  {user.is_admin && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                      <LayoutDashboard className="h-4 w-4 mr-2" /> Admin
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                    <LogOut className="h-4 w-4 mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button onClick={() => navigate('/login')} className="hidden sm:flex h-9 bg-accent text-accent-foreground hover:bg-accent/90" data-testid="header-login-button">
                <User className="h-4 w-4 mr-1.5" /> Sign in
              </Button>
            )}

            <MobileNav nav={nav} user={user} onLogout={logout} />
          </div>
        </div>
      </div>
    </header>
  );
};

export const Footer = () => {
  const { navItems } = useApp();
  const nav = (navItems || NAV_FALLBACK).map((n) => ({ label: n.label, to: n.route || n.to }));
  return (
  <footer className="mt-20 bg-primary text-primary-foreground">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="grid gap-8 md:grid-cols-4">
        <div>
          <div className="flex items-center gap-2">
            <img src={brandIcon} alt="FlightDeals.in logo" className="h-8 w-auto" width="49" height="32" />
            <span className="font-display font-semibold text-lg">FlightDeals<span className="text-accent">.in</span></span>
          </div>
          <p className="mt-3 text-sm text-primary-foreground/70 max-w-xs">
            A premium travel metasearch & comparison platform. We compare deals — partners handle the booking.
          </p>
        </div>
        <div>
          <h4 className="font-semibold text-sm mb-3">Explore</h4>
          <ul className="space-y-2 text-sm text-primary-foreground/70">
            {nav.map((n) => <li key={n.to}><Link to={n.to} className="hover:text-accent">{n.label}</Link></li>)}
          </ul>
        </div>
        <div>
          <h4 className="font-semibold text-sm mb-3">Popular</h4>
          <ul className="space-y-2 text-sm text-primary-foreground/70">
            <li><Link to="/flights/kolkata-to-tokyo" className="hover:text-accent">Kolkata → Tokyo</Link></li>
            <li><Link to="/cheap-flights-from-kolkata" className="hover:text-accent">Cheap flights from Kolkata</Link></li>
            <li><Link to="/destinations/bali" className="hover:text-accent">Bali travel guide</Link></li>
            <li><Link to="/deals" className="hover:text-accent">Today's deals</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold text-sm mb-3">Good to know</h4>
          <ul className="space-y-2 text-sm text-primary-foreground/70">
            <li>No booking fees</li>
            <li>We never take payment</li>
            <li>Book securely on partner sites</li>
            <li><Link to="/help" className="hover:text-accent font-medium text-accent/90" data-testid="footer-help-link">Help &amp; contact us</Link></li>
            <li><Link to="/privacy" className="hover:text-accent" data-testid="footer-privacy-link">Privacy Policy</Link></li>
            <li><Link to="/cookies" className="hover:text-accent" data-testid="footer-cookies-link">Cookie Policy</Link></li>
          </ul>
        </div>
      </div>
      <div className="mt-10 pt-6 border-t border-white/10">
        <AffiliateDisclosure variant="footer" />
        <p className="mt-3 text-xs text-primary-foreground/50">© {new Date().getFullYear()} FlightDeals.in — Metasearch & comparison only. Not an OTA, travel agency or booking engine.</p>
      </div>
    </div>
  </footer>
  );
};

const VerticalUnavailable = () => (
  <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="vertical-unavailable">
    <div className="mx-auto mb-4 grid place-items-center h-14 w-14 rounded-2xl bg-secondary">
      <PackageX className="h-7 w-7 text-muted-foreground" />
    </div>
    <h1 className="font-display text-2xl font-semibold">This section is currently unavailable</h1>
    <p className="mt-2 text-sm text-muted-foreground">
      We've temporarily turned off this part of the site. Please explore our other travel sections in the meantime.
    </p>
    <div className="mt-6">
      <Link to="/" className="inline-flex items-center rounded-xl bg-accent text-accent-foreground px-4 py-2 text-sm font-medium hover:bg-accent/90" data-testid="unavailable-home-link">
        Back to home
      </Link>
    </div>
  </div>
);

export const Layout = ({ children }) => {
  const location = useLocation();
  const { isRouteEnabled } = useApp();
  const enabled = isRouteEnabled ? isRouteEnabled(location.pathname) : true;

  useEffect(() => {
    // Disabled verticals must not be indexed while turned off.
    if (enabled) return undefined;
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, [enabled]);

  return (
    <div className="App min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">{enabled ? children : <VerticalUnavailable />}</main>
      <Footer />
      <EsimCrossSellModal />
    </div>
  );
};
